import { type RefObject, useCallback, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Divider, Icon } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"

import api, { asQuery } from "api"
import type { ItemRefs } from "components/ideas/useIdeaScrollTracking"
import util from "utils/utils"

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor"
import { StageSegment } from "../../ImpactStatements/StageSegments"

type ImpactStatements = OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]

type ImpactStatementManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  onUpdateIdea: (data: any) => void
  impactList: any[]
  sectionRefs?: RefObject<ItemRefs>
  scrollRef?: RefObject<HTMLDivElement>
}

const ImpactStatementManagement = ({ idea, onUpdateIdea, impactList }: ImpactStatementManagementProps) => {
  const { t } = useTranslation()
  const [currentImpactStage, setCurrentImpactStage] = useState<ImpactStatements["userEstimate"]["stage"] | null>(null)
  const [isCreatingStage, setIsCreatingStage] = useState(null)
  const [detailsViewStage, setDetailsViewStage] = useState<string | null>(null)

  const impactStatementQuery = useQuery({
    queryKey: ["ideas", idea._id, "impactStatement"],
    queryFn: asQuery(api.ideas.getImpactStatements, {
      params: [idea._id, null],
    })!,
  })

  const createImpactStatement = useCallback(
    (newStatement: ImpactStatements["userEstimate"], forStage: OpenAPI.Schemas["ImpactStatement"]["stage"]) => {
      setIsCreatingStage(forStage)
      api.ideas.createImpactStatement(
        idea._id,
        { impacts: newStatement.impacts, description: newStatement.description, stage: forStage },
        null,
        ({ impactStatement: newImpactStatement }) => {
          setIsCreatingStage(null)
          api.queryClient.setQueryData(["ideas", idea._id, "impactStatement"], (prev: any) => {
            return {
              ...prev,
              impactStatements: {
                ...prev.impactStatements,
                [forStage]: newImpactStatement,
              },
            }
          })
          setCurrentImpactStage(forStage)
          onUpdateIdea({
            completedImpactStages: {
              ...(idea.completedImpactStages || {}),
              [forStage]: true,
            },
          })
        },
        () => {
          setIsCreatingStage(null)
          toast.error(`Failed to save ${t("generic.impact")} statement. Please try again.`)
        },
      )
    },
    [idea._id, t, onUpdateIdea, idea.completedImpactStages],
  )

  const throttledUpdate = useThrottle(onUpdateIdea, 400, [onUpdateIdea])
  const updateImpactStatements = useCallback(
    (impactStatements: ImpactStatements) => {
      api.queryClient.setQueryData(["ideas", idea._id, "impactStatement"], { impactStatements })

      const mostImportantStatement =
        impactStatements.finalOutcome || impactStatements.projectEstimate || impactStatements.userEstimate
      throttledUpdate({
        totalImpacts: Object.entries(mostImportantStatement.impacts)
          .filter(([_key, value]) => value.enabled)
          .map(([key, value]) => {
            const impactOptions = idea?.ownerChallenge?.impactStatements?.impacts?.[key]?.options || []
            let impactOption = {}

            let maxWeight = 1
            for (const option of impactOptions) {
              if (option.id === value?.choice?.id) {
                impactOption = option
              }
              if (option.weight == null) continue
              if (option.weight > maxWeight) {
                maxWeight = option.weight
              }
            }

            return {
              impactId: key,
              ownerImpact: impactList.find((impact) => impact._id === key),
              ...value,
              choice: {
                ...(value.choice || {}),
                ...(impactOption || {}),
              },
              maxWeight,
            }
          }),
      })
    },
    [throttledUpdate, impactList, idea._id, idea?.ownerChallenge?.impactStatements],
  )

  const deleteStatement = (impactStatementId: string) => {
    api.ideas.deleteImpactStatement(
      idea._id,
      impactStatementId,
      () => {
        toast.success("Statement deleted successfully")
        api.queryClient.setQueryData(
          ["ideas", idea._id, "impactStatement"],
          (prev: { impactStatements: ImpactStatements }) => {
            const newStatements = { ...(prev.impactStatements || {}) }
            const deletedStatement = Object.entries(newStatements).find(
              ([_, statement]) => statement._id === impactStatementId,
            )
            if (deletedStatement) {
              delete newStatements[deletedStatement[0]]
            }
            return { impactStatements: newStatements }
          },
        )
      },
      () => {
        toast.error("Failed to delete statement")
      },
    )
  }

  if (currentImpactStage) {
    return (
      <div>
        <Button basic compact onClick={() => setCurrentImpactStage(null)}>
          <Icon name="arrow left" />
          Back to overview
        </Button>

        <Divider />

        <ImpactStatementEditor
          impactStatements={impactStatementQuery.data?.impactStatements || {}}
          forStage={currentImpactStage}
          setImpactStatements={updateImpactStatements}
          statementDefintion={idea.ownerChallenge.impactStatements}
          ideaId={idea._id}
          restrictStagesTo={[currentImpactStage]}
          editable={currentImpactStage !== "userEstimate"}
        />

        <Divider hidden />
        <Button
          secondary
          onClick={() => {
            setCurrentImpactStage(null)
          }}
        >
          <Icon name="check" />
          Done
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div>
        {detailsViewStage === "userEstimate" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      {idea.ownerChallenge.impactStatements.showOnIdea ? (
        <StageSegment
          stage="userEstimate"
          statementDefintion={idea.ownerChallenge.impactStatements}
          impactStatement={impactStatementQuery.data?.impactStatements?.userEstimate}
          onView={() => {
            setCurrentImpactStage("userEstimate")
          }}
        />
      ) : null}
      <div>
        {detailsViewStage === "projectEstimate" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      <StageSegment
        stage="projectEstimate"
        statementDefintion={idea.ownerChallenge.impactStatements}
        impactStatement={impactStatementQuery.data?.impactStatements?.projectEstimate}
        onView={() => {
          setCurrentImpactStage("projectEstimate")
        }}
        isCreating={isCreatingStage === "projectEstimate"}
        onCreate={() => {
          createImpactStatement(
            {
              impacts: impactStatementQuery.data?.impactStatements?.userEstimate?.impacts ?? {},
              description: impactStatementQuery.data?.impactStatements?.userEstimate?.description,
            },
            "projectEstimate",
          )
        }}
        onDelete={() => {
          util
            .confirm(
              "Delete the project estimate?",
              "Are you sure you want to delete the project estimate statement? This action is instant and irreversible.",
            )
            .then(() => {
              deleteStatement(impactStatementQuery.data?.impactStatements?.projectEstimate?._id)
              onUpdateIdea({
                completedImpactStages: {
                  ...(idea.completedImpactStages || {}),
                  projectEstimate: false,
                },
              })
            })

            .catch(() => {})
        }}
      />
      <div>
        {detailsViewStage === "finalOutcome" && (
          <Button basic compact onClick={() => setDetailsViewStage(null)}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
        )}
      </div>
      <StageSegment
        stage="finalOutcome"
        statementDefintion={idea.ownerChallenge.impactStatements}
        impactStatement={impactStatementQuery.data?.impactStatements?.finalOutcome}
        onView={() => {
          setCurrentImpactStage("finalOutcome")
        }}
        onCreate={() => {
          createImpactStatement(
            {
              impacts: impactStatementQuery.data?.impactStatements?.projectEstimate?.impacts ?? {},
              description: impactStatementQuery.data?.impactStatements?.projectEstimate?.description,
            },
            "finalOutcome",
          )
        }}
        onDelete={() => {
          util
            .confirm(
              "Delete final outcome?",
              "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
            )
            .then(() => {
              deleteStatement(impactStatementQuery.data?.impactStatements?.finalOutcome?._id)
              onUpdateIdea({
                completedImpactStages: {
                  ...(idea.completedImpactStages || {}),
                  finalOutcome: false,
                },
              })
            })
            .catch(() => {})
        }}
      />
    </div>
  )
}

export default ImpactStatementManagement
